import { type JSX, useMemo } from "react";

import { Polyline } from "@vis.gl/react-google-maps";

import { useMapPalette } from "./MapView.context";
import type { MapRouteProps } from "./MapView.types";
import { DEFAULT_ROUTE_WIDTH, DEFAULT_TONE } from "./constants";
import { decodePolyline } from "./decodePolyline";
import { useMapContent } from "./useMapContent";

export function MapRoute({
  path,
  tone = DEFAULT_TONE,
  width = DEFAULT_ROUTE_WIDTH,
  dashed = false,
  onClick,
}: MapRouteProps): JSX.Element {
  const points = useMemo(
    () => (typeof path === "string" ? decodePolyline(path) : path),
    [path],
  );

  useMapContent(points);

  const palette = useMapPalette();
  const color = palette?.[tone];

  // A dashed line is a transparent line carrying a repeated dash symbol.
  const icons = useMemo<google.maps.IconSequence[] | undefined>(
    () =>
      dashed
        ? [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 0.9,
                strokeColor: color,
                scale: width / 2,
              },
              offset: "0",
              repeat: `${width * 3}px`,
            },
          ]
        : undefined,
    [dashed, color, width],
  );

  return (
    <Polyline
      path={points}
      strokeColor={color}
      strokeWeight={width}
      strokeOpacity={dashed ? 0 : 0.9}
      icons={icons}
      clickable={onClick !== undefined}
      onClick={(event) => {
        if (onClick && event.latLng) {
          onClick(event.latLng.toJSON());
        }
      }}
    />
  );
}
